<template>
  <component
    :is="componentType"
    v-if="componentType !== 'NuxtLink'"
    :class="classes"
    :href="calculatedHref"
    :disabled="disabled !== false ? disabled : null"
  >
    <span class="VisionButton__text">
      <span v-if="!$slots.content">
        {{ buttonText }}
      </span>
      <slot
        v-else-if="$slots.content"
        name="content"
      />
    </span>
  </component>
  <NuxtLink
    v-else
    :class="classes"
    :to="calculatedTo"
    :disabled="disabled !== false ? disabled : null"
  >
    <span class="VisionButton__text">
      <span v-if="!$slots.content">
        {{ buttonText }}
      </span>
      <slot
        v-else-if="$slots.content"
        name="content"
      />
    </span>
  </NuxtLink>
</template>

<script>
const gradientColors = [
  'gradient',
  'gradient-light',
  'gradient-gold',
  'gradient-orange',
  'gradient-yellow',
  'gradient-radial-grey',
  'gradient-miami',
  'gradient-purple',
  'gradient-mclaren-mx',
  'gradient-silverblue',
  'gradient-4ko',
  'gradient-8ko',
  'gradient-rainbow',
  'gradient-yellow-jersey'
]

export default {
  name: 'VisionButton',

  props: {
    to: { type: [String, Object], required: false, default: '' },
    spaLink: { type: Boolean, required: false, default: false },
    buttonText: { type: String, required: false, default: '' },
    square: { type: Boolean, required: false, default: false },
    disabled: { type: Boolean, default: false, required: false },
    element: { type: String, default: '', required: false },
    backgroundColour: {
      type: String,
      default: 'white',
      validator(value) {
        return [
          'white',
          'black',
          'whatsapp',
          'transparent',
          ...gradientColors,
        ].includes(value)
      },
    },
    outline: {
      type: Boolean,
      default: false,
    },
    textColour: {
      type: String,
      default: '',
      validator(value) {
        return ['', 'white', 'black', 'gradient'].includes(value)
      },
    },
    size: {
      type: String,
      default: 'regular',
      validator(value) {
        return [
          'unpadded',
          'xx-small',
          'x-small',
          'small',
          'regular',
          'large',
        ].includes(value)
      },
    },
    buttonStyle: {
      type: String,
      default: 'default',
      validator(value) {
        return ['default', 'plain'].includes(value)
      },
    },
    shadow: {
      type: Boolean,
      default: false,
    },
    hoverColour: {
      default: null,
      validator(value) {
        return [
          null,
          'gradient'
        ].includes(value)
      }
    },
    promoButton: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    classes() {
      return [
        {
          'VisionButton--square': this.square,
          'VisionButton--shadow': this.shadow,
          'VisionButton--outline': this.outline,
          'VisionButton--theme-promo': this.promoButton,
          'VisionButton--with-gradient':
            gradientColors.includes(this.backgroundColour),
          [`VisionButton--hover-${this.hoverColour}`]: this.hoverColour
        },
        `VisionButton--bg-${this.backgroundColour}`,
        `VisionButton--text-${this.textColour}`,
        `VisionButton--size-${this.size}`,
        `VisionButton--style-${this.buttonStyle}`,
        'VisionButton',
      ]
    },

    componentType() {
      if (this.element) {
        return this.element
      } else if(!this.to) {
        return 'button'
      } else if (this.spaLink && this.to) {
        return 'NuxtLink'
      } else {
        return 'a'
      }
    },

    calculatedHref() {
      if (this.componentType === 'a') {
        return this.to
      }

      return null
    },

    calculatedTo() {
      if(this.componentType === 'NuxtLink') {
        return this.to
      }

      return null
    },
  },
}
</script>

<style lang="postcss">
.VisionButton {
  @apply text-center text-sm font-bold text-off-black
    rounded-full
    transition-colors duration-300 ease-in-out
    inline-flex items-center justify-center
    border-0;

  &[disabled] {
    @apply opacity-50;
  }

  --vision-button-bg-gradient: var(--vision-button-bg-gradient);
  --vision-button-bg-colour: var(--vision-button-bg-colour);
  --vision-button-bg-colour--hover: var(--vision-button-bg-colour--hover);
  --vision-button-text-gradient: var(--vision-button-text-gradient);
  --vision-button-text-colour: var(--vision-button-text-colour);
  --vision-button-text-colour--hover: var(--vision-button-text-colour);
  --vision-button-border-colour: var(--vision-button-border-colour);
}

.VisionButton__text {
  @apply block w-full whitespace-nowrap;
}

/* Style */

.VisionButton--style-plain {
  @apply border-none outline-none;
}

/* Radius */

.VisionButton--square {
  @apply rounded-md;
}

/* Text colours */

.VisionButton--text-gradient {
  .VisionButton__text {
    @apply t-gradient;
    --text-gradient-value: var(--vision-button-text-gradient, var(--gradient-default));
    background-image: var(--text-gradient-value);
  }
}

.VisionButton--text-black {
  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.off-black'));
    color: var(--text-colour-value);
  }
}

.VisionButton--text-white {
  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

/* Background colours */

.VisionButton--bg-white {
  @apply border-2;

  --bg-colour-value: var(--vision-button-bg-colour, theme('colors.white'));
  background: var(--bg-colour-value);
  --text-colour-value: var(--vision-button-text-colour, theme('colors.off-black'));
  color: var(--text-colour-value);
  --border-colour-value: var(--vision-button-border-colour, theme('colors.grey-light'));
  border-color: var(--border-colour-value);
  

  &.VisionButton--style-plain {
    @apply border-none outline-none;
  }

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.off-black'));
    color: var(--text-colour-value);
  }

  &.VisionButton--outline {
    @apply bg-transparent;

    --border-colour-value: var(--vision-button-border-colour, theme('colors.white'));
    border-color: var(--border-colour-value);
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);

    .VisionButton__text {
      --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
      color: var(--text-colour-value);
    }

    &:hover {
      --bg-colour-value: var(--vision-button-bg-colour--hover, theme('colors.white'));
      background: var(--bg-colour-value);
      --text-colour-value: var(--vision-button-text-colour, theme('colors.off-black'));
      color: var(--text-colour-value);

      .VisionButton__text {
        --text-colour-value: var(--vision-button-text-colour--hover, theme('colors.off-black'));
        color: var(--text-colour-value);
      }
    }
  }

  &:focus,
  &:active,
  &:focus-within {
    @apply border-transparent;

    box-shadow: 0 0 0 2px theme('colors.blue');
  }
}

.VisionButton--bg-black {
  --bg-colour-value: var(--vision-button-bg-colour, theme('colors.off-black'));
  background: var(--bg-colour-value);
  --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
  color: var(--text-colour-value);

  &.VisionButton--outline {
    @apply bg-transparent border-2;

    --border-colour-value: var(--vision-button-border-colour, theme('colors.off-black'));
    border-color: var(--border-colour-value);
    --text-colour-value: var(--vision-button-text-colour, theme('colors.off-black'));
    color: var(--text-colour-value);

    .VisionButton__text {
      --text-colour-value: var(--vision-button-text-colour, theme('colors.off-black'));
      color: var(--text-colour-value);
    }

    &:hover {
      --bg-colour-value: var(--vision-button-bg-colour--hover, theme('colors.off-black'));
      background: var(--bg-colour-value);
      --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
      color: var(--text-colour-value);

      .VisionButton__text {
        --text-colour-value: var(--vision-button-text-colour--hover, theme('colors.white'));
        color: var(--text-colour-value);
      }
    }
  }
}

.VisionButton--bg-whatsapp {
  @apply text-white bg-whatsapp;

  &.VisionButton--outline {
    @apply bg-transparent border-whatsapp;
  }
}

.VisionButton--bg-transparent {
  @apply bg-transparent;
}

/* Gradients */

.VisionButton--with-gradient {
  @apply bg-[size:_150%_100%] bg-[50%_0%] text-white transition-all duration-300 border-0;

  .VisionButton__text {
    @apply text-white;
  }

  &.VisionButton--outline {
    @apply bg-transparent border-blue;

    .VisionButton__text {
      @apply t-gradient;
    }
  }

  &:hover {
    @apply bg-[0];
  }

  &:focus,
  &:active,
  &:focus-within {
    @apply border-transparent;

    box-shadow: 0 0 0 2px theme('colors.purple');
  }
}

.VisionButton--bg-gradient {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-light {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-light)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-gold {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-gold)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-orange {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-orange)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-yellow {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-yellow)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.off-black'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-radial-grey {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-radial-grey)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.off-black'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-miami {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-miami)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-purple {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-purple)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-mclaren-mx {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-mclaren-mx)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-silverblue {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-silverblue)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-4ko {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-4ko)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-8ko {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-8ko)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-rainbow {
  background-size: 125% 100%;
  background-position: 50% 0;
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--vision-button-bg-colour, var(--gradient-custom)));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.white'));
    color: var(--text-colour-value);
  }
}

.VisionButton--bg-gradient-yellow-jersey {
  --bg-gradient-value: var(--vision-button-bg-gradient, var(--gradient-yellow-jersey));
  background: var(--bg-gradient-value);

  .VisionButton__text {
    --text-colour-value: var(--vision-button-text-colour, theme('colors.off-black'));
    color: var(--text-colour-value);
  }
}

/* Promo Theme */
.VisionButton--theme-promo {
  @apply promo-gradient-border promo-gradient-border-animated transition-all duration-500;
  box-shadow: 0px 0px 6px var(--promo-key), inset 0px 0px 0px var(--promo-highlight);

  .VisionButton__text {
    @apply promo-t-gradient promo-t-gradient-animated transition-[filter] duration-500;
    filter: brightness(1) drop-shadow(0px 0px 0px var(--promo-key));
  }

  &:hover, &:focus {
    border-color: var(--promo-highlight, theme(colors.blue-light));
    box-shadow: 0px 0px 4px var(--promo-highlight), inset 0px 0px 6px var(--promo-highlight);

    .VisionButton__text {
      filter: brightness(1.5) drop-shadow(0px 0px 6px var(--promo-key));
    }
  }
}

/* Size */
.VisionButton--size-xx-small {
  @apply p-0 text-[12px];
}

.VisionButton--size-xx-small {
  @apply px-3 py-1 text-[12px];
}

.VisionButton--size-x-small {
  @apply px-4 py-2 text-xxs;
}

.VisionButton--size-small {
  @apply px-5 py-3 text-xs;
}

.VisionButton--size-regular {
  @apply py-3 px-6 text-sm;
}

.VisionButton--size-large {
  @apply p-4 text-base;
}

.VisionButton--shadow {
  @apply shadow-lg;
}

/* Hover States */
.VisionButton--hover-gradient {
  &:hover,
  &:focus {
    @apply bg-gradient border-0;

    .VisionButton__text {
      @apply text-white;
    }
  }
}
</style>
